import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Select from "react-select";
import {FileText, ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "../css/LoanSettlement.css";

const API_URL = "https://payyug.in/api";

const LoanSettlement = () => {
  const [loans, setLoans] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [loanDetail, setLoanDetail] = useState(null);
  const [emiList, setEmiList] = useState([]);
  const [totalEmi, setTotalEmi] = useState("");
  const [loading, setLoading] = useState(false);

  // Table States
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [settlementData, setSettlementData] = useState({
    amount: "",
    date: new Date().toISOString().split('T')[0], // Default to today
  });
  const [submitting, setSubmitting] = useState(false);

  const superAdminId = localStorage.getItem("adminuserId");

  // Fetch Master Data
  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/get-all-loan?superAdminId=${superAdminId}`);
        const sortedData = res.data.data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setLoans(sortedData || []);
      } catch (err) {
        toast.error("Failed to load loans");
      } finally {
        setLoading(false);
      }
    };
    fetchMasterData();
  }, [superAdminId]);

  // Reusable function to fetch/refresh EMI details
  const fetchLoanDetails = async (idToFetch) => {
    if (!idToFetch) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/get-loanemi/${idToFetch}`);
      setEmiList(res.data.emis || []);
      setTotalEmi(res.data.count);
    } catch (err) {
      setEmiList([]);
      toast.error("EMI details not found");
    } finally {
      setLoading(false);
    }
  };

  // Handle Selection
  const handleLoanChange = async (option) => {
    setSelectedOption(option);
    if (!option) {
      setLoanDetail(null);
      setEmiList([]);
      setTotalEmi("");
      return;
    }

    const detail = loans.find(l => String(l.loanAccountNumber) === String(option.value));
    setLoanDetail(detail);

    const idToFetch = detail.loanId || detail.id || detail._id;
    await fetchLoanDetails(idToFetch);
  };

  const handleSettlementClick = () => {
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettlementData(prev => ({ ...prev, [name]: value }));
  };

  const handleSettlementSubmit = async (e) => {
    e.preventDefault();
    if (!settlementData.amount || !settlementData.date) {
      return toast.warning("Please fill all fields");
    }

    const idToFetch = loanDetail.id || loanDetail.loanId || loanDetail._id;

    try {
      setSubmitting(true);
      const payload = {
        loanId: idToFetch,
        settlementAmount: settlementData.amount,
        settlementDate: settlementData.date,
        superAdminId: superAdminId
      };

      const res = await axios.post(`${API_URL}/loan/settlement`, payload);

      if (res.status === 200) {
        toast.success(res.data.message || "Loan settled successfully!");
        setIsModalOpen(false);
        // Reset form
        setSettlementData({ amount: "", date: new Date().toISOString().split('T')[0] });
        // Refresh EMI list
        await fetchLoanDetails(idToFetch);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Settlement failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Pagination & Search Logic
  const filteredEmi = useMemo(() => {
    return emiList.filter(emi =>
      Object.values(emi).some(val =>
        String(val).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [emiList, searchTerm]);

  const totalPages = Math.ceil(filteredEmi.length / itemsPerPage);
  const currentEmiData = filteredEmi.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const dropdownOptions = loans.map(l => ({
    value: l.loanAccountNumber,
    label: `${l.userName} - ${l.loanAccountNumber}`
  }));

  return (
    <div className="lp-container">
      <ToastContainer position="top-right" autoClose={2000} />

      <div className="lp-header-card">
        <h2 className="lp-main-title">Loan Settlement</h2>
        <div className="lp-search-row">
          <label>Loan</label>
          <Select
            options={dropdownOptions}
            value={selectedOption}
            onChange={handleLoanChange}
            placeholder="Select Loan Account..."
            className="lp-select-box"
            isClearable
          />
        </div>
      </div>

      {loanDetail && (
        <>
          <div className="lp-info-grid">
            <fieldset className="lp-fieldset">
              <legend className="lp-legend">Loan Info</legend>
              <div className="lp-details-row">
                <div className="lp-col"><strong>Loan Account No.</strong><span>{loanDetail.loanAccountNumber || "N/A"}</span></div>
                <div className="lp-col"><strong>User Name</strong><span>{loanDetail.userName || 'N/A'}</span></div>
                <div className="lp-col"><strong>Loan Type</strong><span>{loanDetail.loanType || "N/A"}</span></div>
                <div className="lp-col"><strong>ROI</strong><span>{`${loanDetail.loanRoi}%` || "N/A"}</span></div>
                <div className="lp-col"><strong>ROI Type</strong><span>{loanDetail.loanRoiType || "N/A"}</span></div>
                <div className="lp-col"><strong>Scheme</strong><span>{loanDetail.loanType || "N/A"}</span></div>
                <div className="lp-col"><strong>Loan Amount</strong><span>{loanDetail.loanAmount ? `₹${loanDetail.loanAmount}` : "N/A"}</span></div>
                <div className="lp-col"><strong>Tenure</strong><span>{loanDetail.loanTenure || "N/A"}</span></div>
                <div className="lp-col"><strong>Recovery Frequency</strong><span>{loanDetail.loanRecoveryFrequency}</span></div>
                <div className="lp-col"><strong>Loan Interest</strong><span>{loanDetail.loanInterest ? `₹${loanDetail.loanInterest}` : "N/A"}</span></div>
                <div className="lp-col"><strong>Applied Date</strong><span>{loanDetail.loanAppliedDate ? new Date(loanDetail.loanAppliedDate).toLocaleDateString("en-GB") : "N/A"}</span></div>
                <div className="lp-col"><strong>Disbursal Date</strong><span>{loanDetail.disbursalDate ? new Date(loanDetail.disbursalDate).toLocaleDateString("en-GB") : "N/A"}</span></div>
                <div className="lp-col"><strong>Status</strong><span>{loanDetail.status || 'N/A'}</span></div>
              </div>
            </fieldset>

            <fieldset className="lp-fieldset">
              <legend className="lp-legend">EMI Info</legend>
              <div className="lp-details-row">
                <div className="lp-col"><strong>Total No of EMIs</strong><span>{totalEmi || "N/A"}</span></div>
                {/* <div className="lp-col"><strong>Total No of EMI Paid</strong><span>{emiList.filter(e => e.status === 'Paid').length}</span></div> */}
                <div className="lp-col"><strong>Total Payable Amount</strong><span>{loanDetail.loanPayable ? `₹${loanDetail.loanPayable}` : "N/A"}</span></div>
                <div className="lp-col"><strong>Total Interest</strong><span>{loanDetail.loanInterest ? `₹${loanDetail.loanInterest}` : "N/A"}</span></div>
                <div className="lp-col"><strong>First Emi Date</strong><span>{loanDetail.firstEmiDate ? new Date(loanDetail.firstEmiDate).toLocaleDateString("en-GB") : "N/A"}</span></div>
              </div>
            </fieldset>
          </div>

          <div className="lp-table-card">
            <div className="lp-table-header">
              <h3 className="lp-sub-title">EMI Details</h3>
              <div className="lp-table-controls">
                <div className="lp-entries">
                  Show <select onChange={(e) => setItemsPerPage(Number(e.target.value))}>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select> entries
                </div>
                <div className="lp-search-wrapper">
                  <label>Search:</label>
                  <input type="text" onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="lp-table-responsive">
              <table className="lp-table">
                <thead>
                  <tr>
                    <th>S.No.</th>
                    <th>Loan ID</th>
                    <th>EMI No.</th>
                    <th>EMI</th>
                    <th>Principal</th>
                    <th>Interest</th>
                    <th>Outstanding</th>
                    <th>Due Date</th>
                    <th>Payment Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="10" className="lp-loading"><Loader2 className="spinner" /> Loading...</td></tr>
                  ) : currentEmiData.length > 0 ? (
                    currentEmiData.map((emi, index) => (
                      <tr key={index}>
                        <td>{index + 1 + (currentPage - 1) * itemsPerPage}</td>
                        <td>{loanDetail.loanAccountNumber}</td>
                        <td>{emi.emiNumber || "N/A"}</td>
                        <td>{emi.emiAmount ? `₹${emi.emiAmount}` : "N/A"}</td>
                        <td>{emi.principalComponent ? `₹${emi.principalComponent}` : "N/A"}</td>
                        <td>{emi.interestComponent ? `₹${emi.interestComponent}` : "N/A"}</td>
                        <td>{emi.outstanding ? `₹${emi.outstanding}` : "N/A"}</td>
                        <td>{emi.dueDate ? new Date(emi.dueDate).toLocaleDateString("en-GB") : "N/A"}</td>
                        <td>{emi.paymentDate ? new Date(emi.paymentDate).toLocaleDateString("en-GB") : "N/A"}</td>
                        <td><span className={`lp-status ${emi.status?.toLowerCase()}`}>{emi.status}</span></td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="10" className="lp-empty">No data found</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="lp-table-footer">
              <div className="lp-pagination">
                <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}><ChevronLeft size={16} /></button>
                <span>Page {currentPage} of {totalPages || 1}</span>
                <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}><ChevronRight size={16} /></button>
              </div>

              <button className="lp-loan-settlement-btn" onClick={handleSettlementClick}>
                <FileText size={18} /> Proceed to Loan Settlement
              </button>
            </div>
          </div>
        </>
      )}

      {isModalOpen && (
        <div className="lp-modal-overlay">
          <div className="lp-modal-content">
            <div className="lp-modal-header">
              <h3>Settle Loan Account</h3>
              <button className="lp-close-modal" onClick={() => setIsModalOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSettlementSubmit}>
              <div className="lp-modal-body">
                <div className="lp-form-group">
                  <label>Settlement Amount (₹)</label>
                  <input
                    type="number"
                    name="amount"
                    value={settlementData.amount}
                    onChange={handleInputChange}
                    placeholder="Enter final settlement amount"
                    required
                  />
                </div>
                <div className="lp-form-group">
                  <label>Settlement Date</label>
                  <input
                    type="date"
                    name="date"
                    value={settlementData.date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <p className="lp-modal-note">
                  * Closing loan <strong>{loanDetail?.loanAccountNumber}</strong> for user <strong>{loanDetail?.userName}</strong>.
                </p>
              </div>
              <div className="lp-modal-footer">
                <button type="button" className="lp-btn-cancel" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="lp-btn-confirm" disabled={submitting}>
                  {submitting ? <Loader2 className="spinner" size={16} /> : "Confirm Settlement"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanSettlement;